import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import ParishUsersAutocomplete from 'components/Common/autocomplete/ParishUsersAutocomplete';
import CardWrapper from 'components/Common/CardWrapper';
import Autocomplete from 'components/Common/Autocomplete';
import ParishesAutocomplete from 'components/Common/autocomplete/ParishesAutocomplete';
import { AppDispatch } from 'redux/store';
import { selectCanAccessPortal, selectSelectedParishId, selectStaffRoles } from 'redux/selectors/parishContext';
import { effectiveRoleForParish, hasAtLeastRole, MIN_PORTAL_STAFF_ROLE, parishIdsWithAtLeastRole } from 'api/v1Helpers';
import { sendInvoice } from 'services/sendInvoice';
import routes from 'constants/routes';
import { STRIPE_MAX_AMOUNT_CENTS, STRIPE_MIN_AMOUNT_CENTS } from 'types/paymentRequests';
import { formatAmount } from 'utils/helpers';

export const fieldWidth = { xs: '90%', lg: '70%', xl: '50%' };
export const buttonWidth = { xs: '75%', lg: '55%', xl: '35%' };

const formSchema = yup.object({
    amount: yup
        .string()
        .required('Amount is required.')
        .test('amount-min', `Amount must be at least ${formatAmount(STRIPE_MIN_AMOUNT_CENTS, 'cents')}`, (value) => {
            const cents = Number(value);
            return Number.isFinite(cents) && cents >= STRIPE_MIN_AMOUNT_CENTS;
        })
        .test('amount-max', `Amount must be at most ${formatAmount(STRIPE_MAX_AMOUNT_CENTS, 'cents')}`, (value) => {
            const cents = Number(value);
            return Number.isFinite(cents) && cents <= STRIPE_MAX_AMOUNT_CENTS;
        }),
    recipientId: yup.string().required('Please select a recipient'),
    subject: yup
        .string()
        .required('Description is required')
        .trim()
        .min(1, 'Description is required')
        .max(200, 'Description must be 200 characters or fewer'),
    sendTo: yup.string().required('Send To is required'),
    companySenderCode: yup.string().when('sendTo', (sendTo, schema) => {
        if (sendTo[0] === 'parish') {
            return schema.required('Please select a parish');
        }
        return schema;
    }),
    targetParishId: yup.string().when('sendTo', (sendTo, schema) => {
        if (sendTo[0] === 'parish') {
            return schema.required('Please select a parish');
        }
        return schema;
    }),
});

const sendInvoiceOptions = [
    { label: 'Parish', value: 'parish' },
    { label: 'Parishioner', value: 'parishioner' },
];

type FormValues = yup.InferType<typeof formSchema>;

export default function SendInvoiceForm() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { parish, username, recipientId: prefillRecipientId } = state || {};
    const { name, companyCode, companyId } = parish || {};
    const selectedParishId = useSelector(selectSelectedParishId);
    const staffRoles = useSelector(selectStaffRoles);
    const canAccessPortal = useSelector(selectCanAccessPortal);
    const [modalOpen, setModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
    const [recipientLabel, setRecipientLabel] = useState('');

    /** CLERK+ parish UUIDs from `/me.staffRoles` (incl. inherited PROVIDER_ADMIN entries). */
    const staffedParishIds = useMemo(() => parishIdsWithAtLeastRole(staffRoles, MIN_PORTAL_STAFF_ROLE), [staffRoles]);

    /**
     * A deep link (parish detail → Send Invoice) carries whichever parish was on screen, and the
     * directory lists every parish — not only the ones this user staffs. Honour the prefill only
     * when it is staffed; otherwise drop it so the picker opens empty and usable instead of
     * locked on a value the allowlist can never resolve. `ProtectedRoute` renders this page only
     * after `/me.staffRoles` has hydrated, so this is safe to decide on the first render.
     */
    const deepLinkParishId = companyId ? String(companyId) : '';
    const deepLinkAllowed = !!deepLinkParishId && staffedParishIds.includes(deepLinkParishId);
    const lockedParishName = deepLinkAllowed ? name : '';

    const {
        watch,
        control,
        setValue,
        handleSubmit,
        trigger,
        formState: { errors, isValid },
    } = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            recipientId: prefillRecipientId || '',
            amount: '',
            subject: '',
            sendTo: companyCode || companyId ? 'parish' : prefillRecipientId || username ? 'parishioner' : '',
            companySenderCode: deepLinkAllowed ? companyCode || '' : '',
            targetParishId: deepLinkAllowed ? deepLinkParishId : '',
        },
    });

    const sendTo = watch('sendTo');
    const targetParishId = watch('targetParishId');
    /** Parish the POST targets — must match backend ParishRoleGuard on :parishId. */
    const submitParishId = sendTo === 'parish' ? targetParishId || null : selectedParishId;
    const memberParishId = submitParishId;

    /** CLERK+ for the parish being submitted against (not merely the shell-selected parish). */
    const canCreateForTarget = useMemo(
        () => hasAtLeastRole(effectiveRoleForParish(staffRoles, submitParishId), MIN_PORTAL_STAFF_ROLE),
        [staffRoles, submitParishId]
    );

    const openConfirm = (values: FormValues) => {
        if (!canCreateForTarget) return;
        setPendingValues(values);
        setModalOpen(true);
    };

    const handleConfirmCreate = async () => {
        if (!pendingValues || submitting) return;
        const parishId = pendingValues.sendTo === 'parish' ? pendingValues.targetParishId : selectedParishId;
        if (!parishId) return;
        if (!hasAtLeastRole(effectiveRoleForParish(staffRoles, parishId), MIN_PORTAL_STAFF_ROLE)) {
            return;
        }

        setSubmitting(true);
        try {
            const result = await dispatch(
                sendInvoice({
                    parishId: String(parishId),
                    recipientId: pendingValues.recipientId,
                    amountCents: Number(pendingValues.amount),
                    subject: pendingValues.subject.trim(),
                })
            );
            if (sendInvoice.fulfilled.match(result)) {
                setModalOpen(false);
                navigate(routes.paymentRequests.detail.replace(':id', result.payload.id));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const sendDisabled = !canCreateForTarget || !isValid || submitting || !memberParishId;

    if (!canAccessPortal) {
        return (
            <CardWrapper>
                <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                    Send Invoice
                </Typography>
                <Divider />
                <Typography sx={{ mt: 2 }} variant="body1">
                    You need a parish staff role (Clerk or higher) to create payment requests.
                </Typography>
            </CardWrapper>
        );
    }

    return (
        <CardWrapper>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                Send Invoice
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                Create a payment request for a parish member. Select the recipient and amount. The member is notified by email and can
                approve or decline from the request page.
            </Typography>
            <form onSubmit={handleSubmit(openConfirm)}>
                <Stack width="100%" alignItems="center" gap={4}>
                    <Controller
                        name="sendTo"
                        control={control}
                        render={({ field: { ...rest } }) => (
                            <Autocomplete
                                {...rest}
                                value={watch('sendTo') || null}
                                disabled={!!lockedParishName}
                                isOptionEqualToValue={(option) => option?.value === watch('sendTo')}
                                onChange={(_, item) => {
                                    const value = item?.value;
                                    if (value === 'parishioner') {
                                        setValue('companySenderCode', '');
                                        setValue('targetParishId', '');
                                    }
                                    setValue('recipientId', '');
                                    setRecipientLabel('');
                                    setValue('sendTo', value);
                                    trigger('sendTo');
                                }}
                                getOptionKey={(option) => option?.value}
                                getOptionLabel={(option) => option?.label}
                                options={sendInvoiceOptions}
                                sx={{ width: fieldWidth }}
                                label="Send To"
                                placeholder="Please select a type"
                                error={!!errors.sendTo}
                                helperText={
                                    (errors?.sendTo as { message?: string })?.message ||
                                    (sendTo === 'parish'
                                        ? 'Invoice a member of a parish you staff (Clerk or higher). The parish itself is not billed.'
                                        : undefined)
                                }
                            />
                        )}
                    />

                    {sendTo === 'parish' && (
                        <Controller
                            name="companySenderCode"
                            control={control}
                            render={({ field: { ...rest } }) => (
                                <ParishesAutocomplete
                                    {...rest}
                                    sx={{ width: fieldWidth }}
                                    label="Select a parish"
                                    value={watch('companySenderCode') || null}
                                    matchKey="companyCode"
                                    disabled={!!lockedParishName}
                                    allowedParishIds={staffedParishIds}
                                    onChange={(_, option) => {
                                        setValue('companySenderCode', option?.companyCode || '');
                                        setValue('targetParishId', option?.companyId ? String(option.companyId) : '');
                                        setValue('recipientId', '');
                                        setRecipientLabel('');
                                        trigger(['companySenderCode', 'targetParishId', 'recipientId']);
                                    }}
                                    error={!!errors.companySenderCode || !!errors.targetParishId}
                                    helperText={
                                        (errors?.companySenderCode as { message?: string })?.message ||
                                        (errors?.targetParishId as { message?: string })?.message
                                    }
                                />
                            )}
                        />
                    )}

                    {(sendTo === 'parishioner' || (sendTo === 'parish' && !!memberParishId)) && (
                        <Controller
                            name="recipientId"
                            control={control}
                            render={({ field: { onChange, value, ...rest } }) => (
                                <ParishUsersAutocomplete
                                    {...rest}
                                    parishId={memberParishId}
                                    user={value || username || null}
                                    setUser={(user) => {
                                        const id = user?.userUUID || user?.id || '';
                                        onChange(id);
                                        setRecipientLabel(user?.name || user?.email || '');
                                        trigger('recipientId');
                                    }}
                                    placeholder="Please select a parishioner"
                                    label={sendTo === 'parish' ? 'Recipient (parish member)' : 'Parishioners'}
                                    sx={{ width: fieldWidth }}
                                    error={!!errors.recipientId}
                                    helperText={
                                        (errors?.recipientId as { message?: string })?.message ||
                                        (sendTo === 'parish'
                                            ? 'Payment requests go to a member of the selected parish — not the parish itself.'
                                            : undefined)
                                    }
                                />
                            )}
                        />
                    )}

                    {sendTo && memberParishId && !canCreateForTarget && (
                        <Typography sx={{ width: fieldWidth }} color="error" variant="body2">
                            You need Clerk or higher on the selected parish to create a payment request there.
                        </Typography>
                    )}

                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                type="currency"
                                icon={null}
                                sx={{ width: fieldWidth }}
                                placeholder="$0.00"
                                label="Amount"
                                error={!!errors.amount}
                                helperText={(errors?.amount as { message?: string })?.message}
                            />
                        )}
                    />
                    <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                height={129}
                                sx={{ width: fieldWidth }}
                                placeholder="Type description here . . ."
                                label="Description"
                                minRows={4}
                                maxRows={4}
                                multiline
                                error={!!errors.subject}
                                helperText={(errors?.subject as { message?: string })?.message}
                            />
                        )}
                    />
                    <Button type="submit" sx={{ width: buttonWidth }} title="Send" disabled={sendDisabled} />
                </Stack>
            </form>

            <Modal onClose={() => !submitting && setModalOpen(false)} isOpen={modalOpen} modalTitle="Confirm Send Invoice">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Create a payment request
                        {recipientLabel ? ` for ${recipientLabel}` : ''} for {formatAmount(pendingValues?.amount || 0, 'cents')}?
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, mt: 1, color: 'text.secondary' }}>
                        The recipient will be notified. Email delivery is processed asynchronously.
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={() => setModalOpen(false)} width={'45%'} title="Cancel" disabled={submitting} variant="outlined" />
                        <Button onClick={handleConfirmCreate} width={'45%'} title="Confirm" loading={submitting} disabled={submitting} />
                    </div>
                </>
            </Modal>
        </CardWrapper>
    );
}
